<template>
  <transition name="mega-fade">
    <div v-show="visible" class="nav-mega-menu" @mouseenter="$emit('enter')" @mouseleave="$emit('leave')">
      <div class="container mega-inner">
        <div
          v-for="(column, colIdx) in menu.columns"
          :key="colIdx"
          class="mega-column"
        >
          <h4 class="column-title">{{ column.title }}</h4>
          <template v-for="(item, itemIdx) in column.items">
            <div
              v-if="item.disabled"
              :key="'disabled-' + itemIdx"
              class="mega-item is-disabled"
            >
              <div class="item-head">
                <span class="item-title">{{ item.title }}</span>
                <span v-if="item.isNew" class="item-badge">NEW</span>
              </div>
              <p v-if="item.desc" class="item-desc">{{ item.desc }}</p>
            </div>
            <nav-link
              v-else
              :key="itemIdx"
              :to="item.path"
              :href="item.href || item.path"
              :external="item.external"
              :link-class="['mega-item', { 'is-highlight': item.highlight, 'is-active': isItemActive(item) }]"
              suppress-active-class
              @click="$emit('navigate')"
            >
              <div class="item-head">
                <span class="item-title">{{ item.title }}</span>
                <span v-if="item.isNew" class="item-badge">NEW</span>
              </div>
              <p v-if="item.desc" class="item-desc">{{ item.desc }}</p>
            </nav-link>
          </template>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import NavLink from '@/components/common/NavLink.vue'
import { getNavLinkKey, getUniqueActiveNavKey } from '@/utils/navigation'

export default {
  name: 'NavMegaMenu',
  components: { NavLink },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    menu: {
      type: Object,
      default: () => ({ columns: [] })
    }
  },
  computed: {
    flatItems() {
      if (!this.menu || !Array.isArray(this.menu.columns)) return []
      return this.menu.columns.flatMap(column => column.items || [])
    },
    activeItemKey() {
      return getUniqueActiveNavKey(this.flatItems, this.$route)
    }
  },
  methods: {
    isItemActive(item) {
      if (!this.activeItemKey) return false
      return getNavLinkKey(item) === this.activeItemKey
    }
  }
}
</script>

<style lang="scss" scoped>
.nav-mega-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: $white;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
  border-top: 1px solid $border-color;
  z-index: 1002;
}

.mega-inner {
  display: flex;
  gap: 0;
  padding: 24px 20px 28px;
}

.mega-column {
  flex: 1;
  min-width: 0;
  padding: 0 28px;
  border-right: 1px solid $border-color;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    border-right: none;
    padding-right: 0;
  }
}

.column-title {
  font-size: 14px;
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 2px solid $primary-color;
  display: block;
}

::v-deep .mega-item {
  display: block;
  padding: 10px 14px;
  margin-bottom: 6px;
  border-radius: 6px;
  background: transparent;
  transition: $transition;

  &:hover,
  &.is-active {
    background: $primary-light;

    .item-title {
      color: $primary-color;
    }

    .item-desc {
      color: $text-secondary;
    }
  }

  &.is-highlight {
    background: transparent;
    border: 1px dashed $primary-color;
    text-align: center;

    .item-title {
      color: $primary-color;
    }

    &:hover,
    &.is-active {
      background: $primary-light;
      border-color: $primary-color;

      .item-title {
        color: $primary-color;
      }
    }
  }

  &.is-disabled {
    cursor: default;

    &:hover {
      background: transparent;

      .item-title {
        color: $text-primary;
      }

      .item-desc {
        color: $text-light;
      }
    }
  }
}

.item-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.item-title {
  font-size: 14px;
  font-weight: 600;
  color: $text-primary;
  transition: $transition;
}

.item-badge {
  padding: 1px 6px;
  background: $primary-color;
  color: $white;
  font-size: 10px;
  border-radius: 3px;
  line-height: 1.4;
}

.item-desc {
  font-size: 12px;
  color: $text-light;
  line-height: 1.5;
  margin: 0;
}

.mega-fade-enter-active,
.mega-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.mega-fade-enter,
.mega-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
